// Ekran 07 — Besleme (Günlük Menü).
// Yerleşim ve sınıf adları ref_files/btys-erp-07-besleme.html'den birebir:
//   .crumb · .page-h(.page-t + gün seçici + .page-actions) · .kstrip(5 KPI)
//   .bes-grid( .card[Menü Listesi] · rapor )
//
// Menü sunucuda tutuluyor: her miktar değişikliği PUT /kalemler ile TAM liste
// olarak gönderiliyor, rapor da sunucudan geliyor. İstemcide hesap yapılmıyor —
// aynı sayının iki yerde iki farklı formülle çıkması riskini kaldırıyor.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { tr, sayiyaCevir } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'

import BeslemeRaporu from './BeslemeRaporu'
import KalemEkleDrawer, { KAYNAK } from './KalemEkleDrawer'
import { besleme, type Bant, type MenuKalemi } from './api'

/** Seed'in gün hikâyesi 13–15.08; ekran BSL-2026-0815 ile açılıyor. */
const VARSAYILAN_TARIH = '2026-08-15'

const GUN_ADI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

/**
 * Gün seçicinin etiketi: "15.08 Cumartesi".
 *
 * Gün adı takvimden hesaplanıyor. Referans HTML "15.08 Cuma" yazıyor ama
 * 15.08.2026 bir Cumartesi — elle yazılmış etiketi kopyalamak yerine doğrusu
 * gösteriliyor (bkz. docs/kararlar-ek.md).
 */
function gunEtiketi(iso: string): string {
  const [y, a, g] = iso.split('-').map(Number)
  const d = new Date(Date.UTC(y, a - 1, g))
  return `${String(g).padStart(2, '0')}.${String(a).padStart(2, '0')} ${GUN_ADI[d.getUTCDay()]}`
}

function gunKaydir(iso: string, adim: number): string {
  const [y, a, g] = iso.split('-').map(Number)
  const d = new Date(Date.UTC(y, a - 1, g + adim))
  return d.toISOString().slice(0, 10)
}

/** KPI değerinin rengi — bant sunucudan (§6.3 eşikleri) geliyor. */
function kpiRenk(bant: Bant | undefined): string {
  if (bant === 'izle') return 'k-v amber'
  if (bant === 'kritik') return 'k-v red'
  return 'k-v'
}

/**
 * Menünün bir satırı (.mi). Miktar alanı yerel taslak tutuyor: her tuş vuruşunda
 * sunucuya gitmek yerine blur/Enter'da yazılıyor.
 */
function MenuSatiri({
  kalem,
  pay,
  kilitli,
  yaz,
  sil,
}: {
  kalem: MenuKalemi
  pay: number | null
  kilitli: boolean
  yaz: (miktarT: number) => Promise<unknown>
  sil: () => void
}) {
  const [taslak, setTaslak] = useState(tr(kalem.miktarT, 1))

  // Sunucu değeri değişince (başka satırın kaydı, dün yüklendi…) alan tazelenir.
  useEffect(() => { setTaslak(tr(kalem.miktarT, 1)) }, [kalem.miktarT])

  const asiyor = kalem.kalan !== null && kalem.kalan < 0
  const [etiket, sinif] = KAYNAK[kalem.kaynakTipi ?? 'lit'] ?? KAYNAK.lit

  async function gonder() {
    const t = sayiyaCevir(taslak)
    if (!Number.isFinite(t) || t <= 0) { setTaslak(tr(kalem.miktarT, 1)); return }
    if (t === kalem.miktarT) return

    try {
      await yaz(t)
    } catch {
      // Sunucu reddetti (K26 bakiye aşımı gibi). Sunucudaki değer DEĞİŞMEDİĞİ
      // için tazeleme alanı sıfırlamaz; reddedilen sayı ekranda kalırsa
      // kullanıcı kaydedilmiş sanır — elle geri alıyoruz.
      setTaslak(tr(kalem.miktarT, 1))
    }
  }

  return (
    <div className="mi">
      <div className="mi-t">
        <div className="mi-ad">
          {kalem.stokAd}
          <span className={`src-b ${sinif}`} title={kalem.kaynakNotu}>{etiket}</span>
          {kalem.hij && <span className="src-b src-tesis">Hijyen</span>}
          {kalem.planMi && <span className="src-b src-tesis">Girişsiz</span>}
        </div>
        <div className="mi-sub">
          <b>{kalem.cariUnvan}</b> ·{' '}
          {kalem.planMi ? (
            'hiç mal kabulü yok'
          ) : (
            <>
              bakiye {tr(kalem.bakiye, 1)} t · kalan{' '}
              <b style={{
                color: asiyor
                  ? 'var(--red)'
                  : kalem.kalan !== null && kalem.kalan < 5 ? 'var(--amber-v)' : undefined,
              }}>
                {tr(kalem.kalan, 1)} t
              </b>
            </>
          )}{' '}
          · KM {tr(kalem.etkinKm, 2)}
        </div>
      </div>
      <input
        className="qty"
        value={taslak}
        disabled={kilitli}
        onChange={(e) => setTaslak(e.target.value)}
        onBlur={gonder}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
        style={asiyor ? { borderColor: 'var(--red-bd)', background: 'var(--red-bg)' } : undefined}
      />
      <span className="mi-sub" style={{ width: 8 }}>t</span>
      <span className="pay">{pay === null ? '—' : `${tr(pay, 1)}%`}</span>
      <button
        className="mini-btn"
        type="button"
        title="Kalemi çıkar"
        disabled={kilitli}
        onClick={sil}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        </svg>
      </button>
    </div>
  )
}

function Icerik() {
  const [tarih, setTarih] = useState(VARSAYILAN_TARIH)
  const { ac, kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('Besleme', 'yaz')

  const { data: menu, isLoading } = useQuery({
    queryKey: ['besleme', tarih],
    queryFn: () => besleme.menu(tarih),
  })

  const { data: rapor } = useQuery({
    queryKey: ['besleme-rapor', tarih],
    queryFn: () => besleme.rapor(tarih),
  })

  // Seçici seed'in son gününden geriye bakar; "bugün" ilerlediğinde de
  // hikâyenin günleri listede kalır.
  const { data: gunler = [] } = useQuery({
    queryKey: ['besleme-gunler'],
    queryFn: () => besleme.gunler(VARSAYILAN_TARIH, 7),
  })

  const kilitli = !yazabilir || menu?.durum === 'kaydedildi'

  function tazele() {
    kuyruk.invalidateQueries({ queryKey: ['besleme'] })
    kuyruk.invalidateQueries({ queryKey: ['besleme-rapor'] })
    kuyruk.invalidateQueries({ queryKey: ['besleme-gunler'] })
    // Kaydet havuz bakiyelerini düşürüyor — havuz tarayıcı eskimesin.
    kuyruk.invalidateQueries({ queryKey: ['havuzlar'] })
  }

  /**
   * Kural ihlallerinde (409) sunucu <c>detay[]</c> içinde HANGİ kalemin
   * taştığını söylüyor; yalnız başlığı göstermek kullanıcıyı listede arattırır.
   */
  function hata(h: unknown, varsayilan: string) {
    if (!(h instanceof ApiHatasi)) return toast(varsayilan)
    const detay = h.detay?.length ? ` — ${h.detay.join(' · ')}` : ''
    toast(`${h.message}${detay}`)
  }

  /** Kalem listesini TAM liste olarak yazar — uç bunu bütün olarak yönetiyor. */
  const kalemYaz = useMutation({
    mutationFn: (g: {
      hedefCurutucuId: string | null
      kalemler: { havuzId: string; miktarT: number }[]
    }) => besleme.kalemler(tarih, g),
    onSuccess: (yeni) => {
      kuyruk.setQueryData(['besleme', tarih], yeni)
      kuyruk.invalidateQueries({ queryKey: ['besleme-rapor', tarih] })
    },
    onError: (h) => { hata(h, 'Menü kaydedilemedi'); tazele() },
  })

  const kaydet = useMutation({
    mutationFn: () => besleme.kaydet(tarih),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => hata(h, 'Menü stoğa işlenemedi'),
  })

  const kalemler = menu?.kalemler ?? []
  const toplamT = useMemo(() => kalemler.reduce((a, k) => a + k.miktarT, 0), [kalemler])
  const planSayisi = kalemler.filter((k) => k.planMi).length

  /** Mevcut listeyi alıp bir değişiklikle geri yazar. */
  /**
   * Menü listesini yazar ve BAŞARILI olursa verilen toast'ı basar.
   *
   * Önce `mutate` çağrılıp toast hemen basılıyordu: sunucu 409 döndüğünde
   * kullanıcı önce "eklendi", hemen ardından hata mesajı görüyordu. Yan etkiyi
   * bildiren metin, yan etki gerçekleştikten sonra çıkmalı (K-UI).
   */
  async function listeyiYaz(
    yeni: { havuzId: string; miktarT: number }[],
    secenek: { basari?: string; hedefCurutucuId?: string | null } = {},
  ) {
    const hedefCurutucuId =
      secenek.hedefCurutucuId === undefined
        ? (menu?.hedefCurutucuId ?? null)
        : secenek.hedefCurutucuId

    try {
      await kalemYaz.mutateAsync({ hedefCurutucuId, kalemler: yeni })
      if (secenek.basari) toast(secenek.basari)
    } catch {
      // Hata toast'ı kalemYaz.onError'da basılıyor.
    }
  }

  const mevcut = () => kalemler.map((k) => ({ havuzId: k.havuzId, miktarT: k.miktarT }))

  /** Reddedilirse satır alanını geri alabilmek için söz döner. */
  function miktarYaz(havuzId: string, miktarT: number) {
    return kalemYaz.mutateAsync({
      hedefCurutucuId: menu?.hedefCurutucuId ?? null,
      kalemler: mevcut().map((k) => (k.havuzId === havuzId ? { ...k, miktarT } : k)),
    })
  }

  function kalemSil(havuzId: string, ad: string) {
    void listeyiYaz(mevcut().filter((k) => k.havuzId !== havuzId), {
      basari: `${ad} menüden çıkarıldı — rapor yeniden hesaplandı`,
    })
  }

  function kalemEkleAc() {
    const id = ac({
      baslik: 'Menüye kalem ekle',
      altBaslik: 'Firma × ürün eşleşmesi — her eşleşme özel bir üründür',
      boyut: 'sm',
      icerik: (
        <KalemEkleDrawer
          kapat={() => kapat(id)}
          ekle={(havuzId, miktarT, planMi) => {
            const liste = mevcut()
            const varsa = liste.find((k) => k.havuzId === havuzId)

            let basari: string
            if (varsa) {
              varsa.miktarT += miktarT
              basari = `Mevcut kalemle birleştirildi (+${tr(miktarT, 1)} t)`
            } else {
              liste.push({ havuzId, miktarT })
              basari = planMi
                ? 'Girişsiz eşleşme plan kalemi olarak eklendi — stok düşümü yapılmaz (K27)'
                : `Menüye eklendi (+${tr(miktarT, 1)} t)`
            }

            void listeyiYaz(liste, { basari })
            kapat(id)
          }}
        />
      ),
    })
  }

  /**
   * "Dünün menüsünü yükle" — dünkü menüyü okuyup bugüne TAM liste olarak yazar.
   * Ayrı bir uç yerine iki mevcut çağrı; dünün kalemleri bugünün bakiyesiyle
   * doğrulanır (aşan kalem 409 ile geri gelir).
   */
  const dunuYukle = useMutation({
    mutationFn: async () => {
      const dun = await besleme.menu(gunKaydir(tarih, -1))
      if (dun.kalemler.length === 0) throw new ApiHatasi(409, 'dun_bos', 'Dünün menüsü boş.')
      return besleme.kalemler(tarih, {
        hedefCurutucuId: dun.hedefCurutucuId,
        kalemler: dun.kalemler.map((k) => ({ havuzId: k.havuzId, miktarT: k.miktarT })),
      })
    },
    onSuccess: (yeni) => {
      kuyruk.setQueryData(['besleme', tarih], yeni)
      kuyruk.invalidateQueries({ queryKey: ['besleme-rapor', tarih] })
      toast(`${gunEtiketi(gunKaydir(tarih, -1))} menüsü yüklendi — düzenleyebilirsiniz`)
    },
    onError: (h) => hata(h, 'Dünün menüsü yüklenemedi'),
  })

  const olrGosterge = rapor?.gostergeler.find((g) => g.ad.startsWith('Organik'))
  const hrtGosterge = rapor?.gostergeler.find((g) => g.ad.startsWith('Hacimsel'))
  const hedefAdi =
    menu?.hedefler.find((h) => h.id === (menu.hedefCurutucuId ?? null))?.ad ?? 'Tüm çürütücüler'

  return (
    <>
      <div className="crumb">Operasyon <b>/ Besleme</b></div>
      <div className="page-h">
        <h1 className="page-t">Besleme — Günlük Menü</h1>
        <select className="sel" value={tarih} onChange={(e) => setTarih(e.target.value)}>
          {gunler.map((g) => (
            <option key={g.tarih} value={g.tarih}>
              {gunEtiketi(g.tarih)} — {g.durum === 'kaydedildi' ? 'Kaydedildi' : 'Taslak'}
            </option>
          ))}
        </select>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled={kilitli || dunuYukle.isPending}
            onClick={() => dunuYukle.mutate()}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Dünün menüsünü yükle
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={kilitli || kalemler.length === 0 || kaydet.isPending}
            onClick={() => kaydet.mutate()}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            Menüyü kaydet — tüketimi işle
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Toplam Besleme</div>
          <div className="k-v">{tr(toplamT, 1)}</div>
          <div className="k-s">t/gün · {kalemler.length} kalem</div>
        </div>
        <div className="k">
          <div className="k-l">Organik Yük (OLR)</div>
          <div className={kpiRenk(olrGosterge?.bant)}>{tr(rapor?.olr ?? null, 2)}</div>
          <div className="k-s">kg oKM/m³·gün · {hedefAdi}</div>
        </div>
        <div className="k">
          <div className="k-l">Alıkonma (HRT)</div>
          <div className={kpiRenk(hrtGosterge?.bant)}>{tr(rapor?.hrtHacimsel ?? null, 0)}</div>
          <div className="k-s">gün · hacimsel, hedef 40–80</div>
        </div>
        <div className="k">
          <div className="k-l">Beklenen Biyogaz</div>
          <div className="k-v">{tr(rapor?.biyogaz ?? null, 0)}</div>
          <div className="k-s">Nm³/gün · CH₄ %{tr(rapor?.ch4Ortalama ?? null, 1)}</div>
        </div>
        <div className="k">
          <div className="k-l">Beklenen Elektrik</div>
          <div className="k-v">{tr(rapor?.elektrik ?? null, 0)}</div>
          <div className="k-s">
            kWh/gün · kojen yükü %
            {tr(rapor?.elektrik == null ? null : (rapor.elektrik / 24000) * 100, 0)}
          </div>
        </div>
      </div>

      <div className="bes-grid">
        <div className="card">
          <div className="toolbar">
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--navy-800)' }}>
              Menü Listesi
            </span>
            <span className="cell-sub">değer kaynağı: analiz → tesis → literatür (K13)</span>
            <div className="toolbar-right">
              <button
                className="btn btn-sec btn-sm"
                type="button"
                disabled={kilitli}
                onClick={kalemEkleAc}
              >
                + Kalem ekle
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="insp-empty">Menü yükleniyor…</div>
          ) : kalemler.length === 0 ? (
            <div className="insp-empty">Menü boş — "+ Kalem ekle" ile başlayın.</div>
          ) : (
            kalemler.map((k) => (
              <MenuSatiri
                key={k.id}
                kalem={k}
                pay={toplamT > 0 ? (k.miktarT / toplamT) * 100 : null}
                kilitli={kilitli}
                yaz={(t) => miktarYaz(k.havuzId, t)}
                sil={() => kalemSil(k.havuzId, k.stokAd)}
              />
            ))
          )}

          <div className="grid-foot">
            <span>
              Σ {tr(toplamT, 1)} t · {tr(rapor?.okmToplam ?? null, 2)} t oKM ·{' '}
              {kalemler.length} kalem · {planSayisi} plan
            </span>
          </div>
        </div>

        {rapor && menu && (
          <BeslemeRaporu
            rapor={rapor}
            hedefler={menu.hedefler}
            hedefId={menu.hedefCurutucuId}
            hedefDegis={(id) =>
              void listeyiYaz(mevcut(), {
                hedefCurutucuId: id,
                basari: 'Hedef çürütücü değişti — OLR/HRT yeniden hesaplandı (K10)',
              })
            }
            kilitli={kilitli}
          />
        )}
      </div>
    </>
  )
}

export default function BeslemeSayfasi() {
  return (
    <Kabuk listeBasligi="Besleme">
      <YetkiKapisi modul="Besleme">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
